/**
 * Journal Anomaly Engine — Wave D-2.
 *
 * Pure, read-only derivation over the posted journal. Surfaces entries that
 * deserve a human eyeball before the period closes. No mutations, no writes.
 *
 * Signals (deterministic, no LLM):
 *   • amount_outlier   — line.amount is a z-score outlier vs same account/period
 *   • duplicate_suspect — same source+amount+account posted within 7 days
 *   • round_number     — large round-dollar amount (>= $1,000 and % $500 == 0)
 *   • weekend_post     — posted on Sat/Sun (review-worthy, not invalid)
 *   • missing_memo     — manual JE >= $500 with no line memos
 *
 * Confidence is a 0–100 self-assessment of how *clean* the entry looks.
 * Lower confidence ⇒ higher review priority. Anomaly flags reduce confidence.
 */
import type { JournalEntry } from "./glTypes";

export type AnomalyKind =
  | "amount_outlier"
  | "duplicate_suspect"
  | "round_number"
  | "weekend_post"
  | "missing_memo";

export interface AnomalyFlag {
  kind: AnomalyKind;
  /** One-sentence plain-English explanation. */
  reason: string;
}

export interface ReviewItem {
  entry: JournalEntry;
  confidence: number;        // 0..100, higher = cleaner
  flags: AnomalyFlag[];
  /** Largest absolute line amount on the entry (helpful sort key). */
  magnitude: number;
}

/** Materiality floor: tiny postings (under this magnitude) are filtered out
 *  of the review queue regardless of how many flags they earn. Coffee runs and
 *  $12 reimbursements shouldn't elbow real findings out of the queue. */
export const MATERIALITY_FLOOR_USD = 250;

const CENT = (n: number) => Math.round(n * 100);

/** ISO weekday: 0 = Sunday, 6 = Saturday. */
function isWeekend(iso: string): boolean {
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  const day = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return day === 0 || day === 6;
}

/** z-score across a numeric series; returns 0 for tiny populations. */
function zScores(values: number[]): number[] {
  if (values.length < 4) return values.map(() => 0);
  const mean = values.reduce((s, v) => s + v, 0) / values.length;
  const variance = values.reduce((s, v) => s + (v - mean) ** 2, 0) / values.length;
  const stdev = Math.sqrt(variance);
  if (stdev === 0) return values.map(() => 0);
  return values.map(v => (v - mean) / stdev);
}

export function detectAnomalies(entries: JournalEntry[]): ReviewItem[] {
  const posted = entries.filter(e => e.status === "posted");

  // 1. Pre-compute z-scores per (account, period) bucket.
  type Bucket = { entryIds: string[]; amounts: number[] };
  const buckets = new Map<string, Bucket>();
  for (const je of posted) {
    for (const l of je.lines) {
      const key = `${l.accountId}|${je.period}`;
      const b = buckets.get(key) ?? { entryIds: [], amounts: [] };
      b.entryIds.push(je.id);
      b.amounts.push(l.amount);
      buckets.set(key, b);
    }
  }
  const outlierEntryIds = new Set<string>();
  const outlierContext = new Map<string, number>(); // entryId -> max |z|
  for (const b of buckets.values()) {
    const zs = zScores(b.amounts);
    for (let i = 0; i < zs.length; i += 1) {
      if (Math.abs(zs[i]) >= 2.5) {
        outlierEntryIds.add(b.entryIds[i]);
        const prev = outlierContext.get(b.entryIds[i]) ?? 0;
        if (Math.abs(zs[i]) > prev) outlierContext.set(b.entryIds[i], Math.abs(zs[i]));
      }
    }
  }

  // 2. Duplicate-suspect index: same source + first-account + amount within 7 days.
  type DupKey = string;
  const dupIndex = new Map<DupKey, JournalEntry[]>();
  for (const je of posted) {
    const firstLine = je.lines[0];
    if (!firstLine) continue;
    const totalCents = je.lines
      .filter(l => l.side === "debit")
      .reduce((s, l) => s + CENT(l.amount), 0);
    const k = `${je.source}|${firstLine.accountId}|${totalCents}`;
    const arr = dupIndex.get(k) ?? [];
    arr.push(je);
    dupIndex.set(k, arr);
  }
  const duplicateEntryIds = new Set<string>();
  for (const arr of dupIndex.values()) {
    if (arr.length < 2) continue;
    arr.sort((a, b) => a.postingDate.localeCompare(b.postingDate));
    for (let i = 1; i < arr.length; i += 1) {
      const prev = new Date(arr[i - 1].postingDate).getTime();
      const cur = new Date(arr[i].postingDate).getTime();
      const days = (cur - prev) / 86_400_000;
      if (days <= 7) {
        duplicateEntryIds.add(arr[i].id);
        duplicateEntryIds.add(arr[i - 1].id);
      }
    }
  }

  // 3. Per-entry assembly.
  const items: ReviewItem[] = posted.map(je => {
    const flags: AnomalyFlag[] = [];
    const magnitude = je.lines.reduce((m, l) => Math.max(m, l.amount), 0);

    if (outlierEntryIds.has(je.id)) {
      const z = outlierContext.get(je.id) ?? 0;
      flags.push({
        kind: "amount_outlier",
        reason: `Line amount is ${z.toFixed(1)}σ above the typical posting to this account this period.`,
      });
    }
    if (duplicateEntryIds.has(je.id)) {
      flags.push({
        kind: "duplicate_suspect",
        reason: "Another entry with the same source, account, and amount posted within 7 days.",
      });
    }
    const bigRound = je.lines.some(l => l.amount >= 1000 && l.amount % 500 === 0);
    if (bigRound) {
      flags.push({
        kind: "round_number",
        reason: "Includes a large round-dollar amount — verify it isn't a placeholder.",
      });
    }
    if (isWeekend(je.postingDate)) {
      flags.push({
        kind: "weekend_post",
        reason: "Posted on a weekend — confirm the date is intentional.",
      });
    }
    if (
      je.source === "manual"
      && magnitude >= 500
      && je.lines.every(l => !l.memo || l.memo.trim() === "")
    ) {
      flags.push({
        kind: "missing_memo",
        reason: "Manual entry over $500 with no line memos — add context for future readers.",
      });
    }

    // Confidence: start at 100, deduct per flag (weighted).
    const weights: Record<AnomalyKind, number> = {
      amount_outlier: 30,
      duplicate_suspect: 35,
      round_number: 10,
      weekend_post: 8,
      missing_memo: 12,
    };
    const deduction = flags.reduce((s, f) => s + weights[f.kind], 0);
    const confidence = Math.max(0, 100 - deduction);

    return { entry: je, confidence, flags, magnitude };
  });

  return items;
}

/** Items needing review, lowest confidence first, then largest magnitude.
 *  Immaterial entries (< floor) are dropped so the queue stays focused on
 *  findings worth a human's attention. Floor is configurable per company
 *  via accountingSettings; the exported MATERIALITY_FLOOR_USD remains the
 *  default fallback for callers that don't pass an override. */
export function getReviewQueue(entries: JournalEntry[], floorUsd?: number): ReviewItem[] {
  const floor = typeof floorUsd === "number" ? Math.max(0, floorUsd) : MATERIALITY_FLOOR_USD;
  return detectAnomalies(entries)
    .filter(i => i.flags.length > 0 && i.magnitude >= floor)
    .sort((a, b) =>
      a.confidence - b.confidence
      || b.magnitude - a.magnitude
      || a.entry.postingDate.localeCompare(b.entry.postingDate),
    );
}

export const ANOMALY_LABEL: Record<AnomalyKind, string> = {
  amount_outlier: "Amount outlier",
  duplicate_suspect: "Possible duplicate",
  round_number: "Round-dollar amount",
  weekend_post: "Weekend post",
  missing_memo: "Missing memo",
};
